/**
 * Centralized styling system for form field components
 *
 * This module provides reusable styles and utilities for consistent
 * form field appearance and behavior across the application.
 */

import {
  cssFontSizeMedium,
  formUi,
  inputMaxWidth,
} from '../constants/globalcss';

/** Placeholder contrast on white — darker than theme `text.secondary` / raw `#888`. */
const PLACEHOLDER_INK = 'rgba(51, 51, 64, 0.68)';

/**
 * Common base styles for all TextField components
 */
export const getBaseTextFieldStyles = (width?: string) => ({
  '& .MuiFormHelperText-root': {
    marginTop: formUi.field.helperSpacingTop,
  },
  '& .MuiInputBase-input::placeholder': {
    WebkitTextFillColor: PLACEHOLDER_INK,
    color: PLACEHOLDER_INK,
    opacity: 1,
  },
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: formUi.field.borderDefault,
  },
  '& .MuiOutlinedInput-root': {
    borderRadius: formUi.card.radius,
  },
  '& .MuiInputBase-root': {
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: formUi.field.borderFocus,
      borderWidth: 2,
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: formUi.field.borderHover,
    },
    backgroundColor: '#fcfdff',
    borderRadius: formUi.card.radius,
    minHeight: formUi.field.inputHeight,
    fontSize: '15px',
  },
  fontSize: cssFontSizeMedium,
  p: 0,
  width: width || inputMaxWidth,
});

/**
 * Common base styles for FormControl components (dropdowns, etc.)
 */
export const getBaseFormControlStyles = (width?: string) => ({
  '& .MuiFormHelperText-root': {
    marginTop: formUi.field.helperSpacingTop,
  },
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: formUi.field.borderDefault,
  },
  '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: formUi.field.borderFocus,
    borderWidth: 2,
  },
  '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: formUi.field.borderHover,
  },
  '& .MuiOutlinedInput-root': {
    backgroundColor: '#fcfdff',
    borderRadius: formUi.card.radius,
    minHeight: formUi.field.inputHeight,
  },
  '& .MuiSelect-select': {
    fontSize: '15px',
    textAlign: 'left',
  },
  p: 0,
  width: width || inputMaxWidth,
});

/**
 * Enhanced styles for TextArea components
 */
export const getTextAreaStyles = (width?: string, isNearLimit?: boolean) => ({
  '& .MuiFormHelperText-root': {
    color: isNearLimit ? '#ff9800' : undefined,
    fontSize: '12px',
  },
  '& .MuiInputBase-input::placeholder': {
    WebkitTextFillColor: PLACEHOLDER_INK,
    color: PLACEHOLDER_INK,
    opacity: 1,
  },
  '& .MuiOutlinedInput-root': {
    borderRadius: formUi.card.radius,
  },
  '& .MuiInputBase-root': {
    backgroundColor: '#fcfdff',
    borderRadius: formUi.card.radius,
    fontSize: '15px',
    lineHeight: '1.4',
  },
  fontSize: cssFontSizeMedium,
  p: 0,
  width: width || inputMaxWidth,
});

/**
 * Common accessibility props for input fields
 */
interface FieldAccessibilityInfo {
  labelId: number;
  text: string;
  isMandatory: boolean;
}

export const getAccessibilityProps = (
  fieldId: string,
  labelId: string,
  errorId: string,
  field: FieldAccessibilityInfo,
  isLabelHidden: boolean,
  hasError: boolean
) => ({
  'aria-describedby': hasError ? errorId : undefined,
  'aria-invalid': hasError,
  'aria-label': isLabelHidden ? field.text : undefined,
  'aria-labelledby': isLabelHidden ? undefined : labelId,
  'aria-required': field.isMandatory,
});

/**
 * Common slotProps for form helper text
 */
export const getFormHelperTextSlotProps = (
  hasError: boolean,
  errorId: string
) => ({
  formHelperText: {
    id: hasError ? errorId : undefined,
  },
});

/**
 * Standard field sizes and spacing
 */
export const fieldDimensions = {
  defaultWidth: inputMaxWidth,
  fontSize: {
    field: cssFontSizeMedium,
    helper: '12px',
    input: '14px',
  },
  padding: 1,
};

/**
 * Common field states and their corresponding styles
 */
export const fieldStates = {
  disabled: {
    opacity: 0.6,
    pointerEvents: 'none',
  },
  error: {
    borderColor: '#d32f2f',
    color: '#d32f2f',
  },
  focus: {
    borderColor: '#1976d2',
    boxShadow: '0 0 0 2px rgba(25, 118, 210, 0.2)',
  },
};

/**
 * Utility to generate consistent field names
 */
export const getFieldName = (field: FieldAccessibilityInfo) =>
  `field-${field.labelId}`;

/**
 * Utility to generate consistent helper text
 */
export const getHelperText = (errorText?: string) => {
  if (errorText) return errorText;
  return '';
};

/**
 * Common computed values for field components
 */
export const getComputedFieldValues = (
  errorText?: string,
  disabled = false,
  displayOnly = false,
  width?: string,
  maxLength = 255
) => ({
  effectiveMaxLength: Math.min(maxLength, 255),
  fieldWidth: width || inputMaxWidth,
  hasError: Boolean(errorText),
  // Derived from errorText like hasError, so it belongs with it rather than being
  // recomputed by every field component.
  helperText: getHelperText(errorText),
  isDisabled: disabled || displayOnly,
});

/**
 * Theme colors for consistent appearance
 */
export const fieldColors = {
  background: {
    default: '#ffffff',
    disabled: '#f5f5f5',
  },
  border: {
    default: '#e0e0e0',
    error: '#d32f2f',
    focus: '#1976d2',
  },
  error: '#d32f2f',
  primary: '#1976d2',
  success: '#4caf50',
  text: {
    disabled: '#bdbdbd',
    primary: '#212121',
    secondary: '#757575',
  },
  warning: '#ff9800',
};

/**
 * Standard input validation limits
 */
export const inputLimits = {
  maxLength: {
    default: 255,
    number: 255,
    percentage: 11,
    text: 255,
    textarea: 32768,
  },
  maxRows: {
    textarea: 8,
  },
  minRows: {
    textarea: 3,
  },
};

/**
 * Presentation for the non-interactive label types. Previously inline literals in
 * FormField's switch, which put display decisions in the dispatcher rather than
 * alongside the other field styling.
 */
export const staticTextStyles = {
  information: { color: 'blue' } as const,
  warning: { color: 'red', fontWeight: 'bold' } as const,
};
